import {
  OptimizationWorkloadPod,
  OptimizationWorkloadPodContainer,
} from '@cast/types';

export type PodRecommendedRequests = {
  cpuCores: number;
  memoryGib: number;
};

export type PodAppliedStatus = 'applied' | 'pending' | 'notApplied';

export const POD_APPLIED_STATUS_RANK: Record<PodAppliedStatus, number> = {
  notApplied: 0,
  pending: 1,
  applied: 2,
};

/**
 * Sum each container's `recommendation.requests`. Containers without a
 * recommendation contribute nothing — never fall back to
 * `resources.requests` (that is the user's current request, not a
 * recommendation).
 */
export const getPodRecommendedRequests = (
  containers: OptimizationWorkloadPodContainer[] | undefined
): PodRecommendedRequests | undefined => {
  if (!containers?.length) {
    return undefined;
  }

  let cpuCores = 0;
  let memoryGib = 0;
  let hasAny = false;

  for (const container of containers) {
    const requests = container.recommendation?.requests;
    if (
      !requests ||
      (requests.cpuCores == null && requests.memoryGib == null)
    ) {
      continue;
    }
    hasAny = true;
    cpuCores += requests.cpuCores ?? 0;
    memoryGib += requests.memoryGib ?? 0;
  }

  return hasAny ? { cpuCores, memoryGib } : undefined;
};

/**
 * Per-pod applied status. `notApplied` covers three cases: missing
 * recommendation, empty `recommendation` object (grpc-gateway can return
 * `{}` without `appliedAt` — never trust truthiness alone), and
 * `isOptimizationDisabled`. `pending` = older recommendation applied,
 * newer one in flight.
 */
export const getPodAppliedStatus = (
  pod: Pick<
    OptimizationWorkloadPod,
    'isLatestRecommendation' | 'recommendation'
  >,
  isOptimizationDisabled = false
): PodAppliedStatus => {
  if (isOptimizationDisabled) {
    return 'notApplied';
  }
  if (!pod.recommendation?.appliedAt) {
    return 'notApplied';
  }
  return pod.isLatestRecommendation ? 'applied' : 'pending';
};
